/**
 * Canvas viewport math adapted from coco-viewer (fit, cursor-anchored zoom,
 * clampPan, draw/move/resize hit testing). Coordinates are CSS pixels.
 * Image space is the caller's original width/height, never the bitmap size.
 * devicePixelRatio only affects the backing-store size.
 */
import { titleRect } from './image-canvas-labels';

export const SCALE_PER_ZOOM_LEVEL = 1.2;
export const MIN_BOX_SIZE = 4;
export const HANDLE_SIZE = 10;
export const HANDLE_HIT_SLOP = 2;
const ESTIMATED_CHAR_WIDTH = 7;

export interface ImagePoint {
    x: number;
    y: number;
}

export interface ImageRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface HitBox {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    label?: string;
    labelBelow?: boolean;
    /** Measured label text width in CSS pixels; estimated when omitted. */
    labelWidth?: number;
}

export type HandleName = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export type PointerAction =
    | { type: 'resize'; id: string; handle: HandleName }
    | { type: 'move'; id: string }
    | { type: 'select-or-draw'; id: string }
    | { type: 'select'; id: string }
    | { type: 'draw' }
    | { type: 'none' };

const HANDLE_CURSORS: Record<HandleName, string> = {
    nw: 'nwse-resize',
    n: 'ns-resize',
    ne: 'nesw-resize',
    e: 'ew-resize',
    se: 'nwse-resize',
    s: 'ns-resize',
    sw: 'nesw-resize',
    w: 'ew-resize',
};

export function cursorForHandle(handle: HandleName): string {
    return HANDLE_CURSORS[handle];
}

export function moveBox(
    start: ImageRect,
    dx: number,
    dy: number,
    imageWidth: number,
    imageHeight: number
): ImageRect {
    return {
        x: clamp(start.x + dx, 0, Math.max(0, imageWidth - start.width)),
        y: clamp(start.y + dy, 0, Math.max(0, imageHeight - start.height)),
        width: start.width,
        height: start.height,
    };
}

export function resizeBox(
    start: ImageRect,
    handle: HandleName,
    dx: number,
    dy: number,
    imageWidth: number,
    imageHeight: number,
    minSize: number = MIN_BOX_SIZE
): ImageRect {
    let x1 = start.x;
    let y1 = start.y;
    let x2 = start.x + start.width;
    let y2 = start.y + start.height;

    if (handle.includes('n')) {
        y1 = clamp(y1 + dy, 0, Math.max(0, y2 - minSize));
    }
    if (handle.includes('s')) {
        y2 = clamp(y2 + dy, y1 + minSize, imageHeight);
    }
    if (handle.includes('w')) {
        x1 = clamp(x1 + dx, 0, Math.max(0, x2 - minSize));
    }
    if (handle.includes('e')) {
        x2 = clamp(x2 + dx, x1 + minSize, imageWidth);
    }

    return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

export function drawBox(
    start: ImagePoint,
    current: ImagePoint,
    imageWidth: number,
    imageHeight: number
): ImageRect {
    const endX = clamp(current.x, 0, imageWidth);
    const endY = clamp(current.y, 0, imageHeight);
    const x = Math.min(start.x, endX);
    const y = Math.min(start.y, endY);
    return {
        x,
        y,
        width: Math.abs(endX - start.x),
        height: Math.abs(endY - start.y),
    };
}

export class ImageCanvasViewport {
    level = 0;
    maxLevel = 20;
    readonly scalePerZoomLevel = SCALE_PER_ZOOM_LEVEL;
    panX = 0;
    panY = 0;
    baseScale = 1;
    areaW = 0;
    areaH = 0;
    imageWidth = 0;
    imageHeight = 0;
    dpr = 1;

    get zoom(): number {
        return Math.pow(this.scalePerZoomLevel, this.level);
    }

    /** CSS pixels per image pixel. */
    get scale(): number {
        return this.baseScale * this.zoom;
    }

    get backingWidth(): number {
        return Math.max(1, Math.round(this.areaW * (this.dpr || 1)));
    }

    get backingHeight(): number {
        return Math.max(1, Math.round(this.areaH * (this.dpr || 1)));
    }

    setSurface(cssWidth: number, cssHeight: number, dpr: number): void {
        this.areaW = Math.max(0, cssWidth);
        this.areaH = Math.max(0, cssHeight);
        this.dpr = dpr > 0 ? dpr : 1;
    }

    setImageSize(width: number, height: number): void {
        this.imageWidth = Math.max(0, width || 0);
        this.imageHeight = Math.max(0, height || 0);
    }

    setMaxLevel(maxLevel: number): void {
        this.maxLevel = Math.max(0, maxLevel);
        if (this.level > this.maxLevel) {
            this.setLevel(this.maxLevel);
        }
    }

    /** Recompute fit from the original image size. Keeps the zoom level. */
    refit(): void {
        this.recomputeBaseScale();
        this.clampPan();
    }

    /** Programmatic zoom, anchored at the canvas center. */
    setLevel(level: number): void {
        this.applyLevel(level, this.areaW / 2, this.areaH / 2);
    }

    /**
     * One wheel notch. direction 1 zooms in (wheel up / deltaY < 0).
     * The image point under (cssX, cssY) stays put.
     */
    zoomAt(cssX: number, cssY: number, direction: 1 | -1): void {
        this.applyLevel(this.level + direction, cssX, cssY);
    }

    panBy(dx: number, dy: number): void {
        if (this.zoom <= 1) {
            this.panX = 0;
            this.panY = 0;
            return;
        }
        this.panX += dx;
        this.panY += dy;
        this.clampPan();
    }

    clampPan(): void {
        if (this.zoom <= 1) {
            this.panX = 0;
            this.panY = 0;
            return;
        }

        const fittedW = this.imageWidth * this.baseScale;
        const fittedH = this.imageHeight * this.baseScale;
        const offsetX = (this.areaW - fittedW) / 2;
        const offsetY = (this.areaH - fittedH) / 2;
        const scaledW = fittedW * this.zoom;
        const scaledH = fittedH * this.zoom;
        this.panX = this.clampAxis(this.panX, this.areaW, offsetX, scaledW);
        this.panY = this.clampAxis(this.panY, this.areaH, offsetY, scaledH);
    }

    private clampAxis(
        pan: number,
        area: number,
        offset: number,
        scaled: number
    ): number {
        if (scaled <= area) {
            return 0;
        }
        const minPan = area - offset - scaled;
        const maxPan = -offset;
        return Math.min(maxPan, Math.max(minPan, pan));
    }

    viewGeometry(): { originX: number; originY: number; scale: number } {
        const fittedW = this.imageWidth * this.baseScale;
        const fittedH = this.imageHeight * this.baseScale;
        return {
            originX: (this.areaW - fittedW) / 2 + this.panX,
            originY: (this.areaH - fittedH) / 2 + this.panY,
            scale: this.scale,
        };
    }

    canvasToImage(cssX: number, cssY: number): ImagePoint {
        const geometry = this.viewGeometry();
        if (!geometry.scale) {
            return { x: 0, y: 0 };
        }
        return {
            x: (cssX - geometry.originX) / geometry.scale,
            y: (cssY - geometry.originY) / geometry.scale,
        };
    }

    imageToCanvas(x: number, y: number): ImagePoint {
        const geometry = this.viewGeometry();
        return {
            x: geometry.originX + x * geometry.scale,
            y: geometry.originY + y * geometry.scale,
        };
    }

    imageRectToCanvas(rect: ImageRect): ImageRect {
        const origin = this.imageToCanvas(rect.x, rect.y);
        return {
            x: origin.x,
            y: origin.y,
            width: rect.width * this.scale,
            height: rect.height * this.scale,
        };
    }

    /** The whole image in canvas pixels, including parts out of view. */
    imageBounds(): ImageRect {
        return this.imageRectToCanvas({
            x: 0,
            y: 0,
            width: this.imageWidth,
            height: this.imageHeight,
        });
    }

    /** Visible part of the image, in image pixels. */
    getVisibleImageRect(): ImageRect {
        const topLeft = this.canvasToImage(0, 0);
        const bottomRight = this.canvasToImage(this.areaW, this.areaH);
        const x = clamp(topLeft.x, 0, this.imageWidth);
        const y = clamp(topLeft.y, 0, this.imageHeight);
        const right = clamp(bottomRight.x, 0, this.imageWidth);
        const bottom = clamp(bottomRight.y, 0, this.imageHeight);
        return { x, y, width: right - x, height: bottom - y };
    }

    containsImagePoint(cssX: number, cssY: number): boolean {
        const point = this.canvasToImage(cssX, cssY);
        return (
            point.x >= 0 &&
            point.y >= 0 &&
            point.x <= this.imageWidth &&
            point.y <= this.imageHeight
        );
    }

    /** Box ids under a pointer, ordered from the top-most box to the back. */
    hitBoxIds(cssX: number, cssY: number, boxes: HitBox[]): string[] {
        const ids: string[] = [];
        for (let index = boxes.length - 1; index >= 0; index--) {
            const box = boxes[index];
            if (
                this.hitBox(cssX, cssY, box) ||
                this.hitTitle(cssX, cssY, box)
            ) {
                ids.push(box.id);
            }
        }
        return ids;
    }

    /**
     * Hit-test order: selected handle, selected box (move), then top-most
     * other box. An unselected box is `select-or-draw` when drawing is
     * enabled so a drag can still start a new box. Empty image area is `draw`.
     */
    classifyPointer(
        cssX: number,
        cssY: number,
        boxes: HitBox[],
        selectedId: string | null,
        editable: boolean,
        drawable = editable
    ): PointerAction {
        const selected = boxes.find((box) => box.id === selectedId);
        if (editable && selected) {
            const handle = this.hitHandle(cssX, cssY, selected);
            if (handle) {
                return { type: 'resize', id: selected.id, handle };
            }
            if (
                this.hitBox(cssX, cssY, selected) ||
                this.hitTitle(cssX, cssY, selected)
            ) {
                return { type: 'move', id: selected.id };
            }
        }

        for (let index = boxes.length - 1; index >= 0; index--) {
            const box = boxes[index];
            if (box.id === selectedId) {
                continue;
            }
            if (
                this.hitBox(cssX, cssY, box) ||
                this.hitTitle(cssX, cssY, box)
            ) {
                return drawable
                    ? { type: 'select-or-draw', id: box.id }
                    : { type: 'select', id: box.id };
            }
        }

        if (
            !editable &&
            selected &&
            (this.hitBox(cssX, cssY, selected) ||
                this.hitTitle(cssX, cssY, selected))
        ) {
            return { type: 'select', id: selected.id };
        }

        if (drawable && this.containsImagePoint(cssX, cssY)) {
            return { type: 'draw' };
        }
        return { type: 'none' };
    }

    private hitHandle(
        cssX: number,
        cssY: number,
        box: HitBox
    ): HandleName | null {
        const rect = this.imageRectToCanvas(box);
        const reach = HANDLE_SIZE / 2 + HANDLE_HIT_SLOP;
        for (const handle of handlePositions(rect)) {
            if (
                Math.abs(cssX - handle.x) <= reach &&
                Math.abs(cssY - handle.y) <= reach
            ) {
                return handle.name;
            }
        }
        return null;
    }

    private hitBox(cssX: number, cssY: number, box: HitBox): boolean {
        const rect = this.imageRectToCanvas(box);
        return (
            cssX >= rect.x &&
            cssX <= rect.x + rect.width &&
            cssY >= rect.y &&
            cssY <= rect.y + rect.height
        );
    }

    private hitTitle(cssX: number, cssY: number, box: HitBox): boolean {
        if (!box.label) {
            return false;
        }
        const title = titleRect(
            this.imageRectToCanvas(box),
            box.labelWidth ?? box.label.length * ESTIMATED_CHAR_WIDTH,
            !!box.labelBelow,
            this.imageBounds()
        );
        return (
            cssX >= title.x &&
            cssX <= title.x + title.width &&
            cssY >= title.y &&
            cssY <= title.y + title.height
        );
    }

    private applyLevel(level: number, anchorX: number, anchorY: number): void {
        const next = clamp(Math.round(level), 0, this.maxLevel);
        const oldZoom = this.zoom;
        if (next === this.level) {
            this.clampPan();
            return;
        }
        this.level = next;
        const ratio = oldZoom === 0 ? 1 : this.zoom / oldZoom;
        const fittedW = this.imageWidth * this.baseScale;
        const fittedH = this.imageHeight * this.baseScale;
        const offsetX = (this.areaW - fittedW) / 2;
        const offsetY = (this.areaH - fittedH) / 2;
        this.panX = anchorX - offsetX - (anchorX - offsetX - this.panX) * ratio;
        this.panY = anchorY - offsetY - (anchorY - offsetY - this.panY) * ratio;
        this.clampPan();
    }

    private recomputeBaseScale(): void {
        if (
            this.areaW <= 0 ||
            this.areaH <= 0 ||
            this.imageWidth <= 0 ||
            this.imageHeight <= 0
        ) {
            this.baseScale = 1;
            return;
        }
        this.baseScale = Math.min(
            this.areaW / this.imageWidth,
            this.areaH / this.imageHeight,
            1
        );
    }
}

export function handlePositions(
    rect: ImageRect
): { name: HandleName; x: number; y: number }[] {
    const midX = rect.x + rect.width / 2;
    const midY = rect.y + rect.height / 2;
    return [
        { name: 'nw', x: rect.x, y: rect.y },
        { name: 'n', x: midX, y: rect.y },
        { name: 'ne', x: rect.x + rect.width, y: rect.y },
        { name: 'e', x: rect.x + rect.width, y: midY },
        { name: 'se', x: rect.x + rect.width, y: rect.y + rect.height },
        { name: 's', x: midX, y: rect.y + rect.height },
        { name: 'sw', x: rect.x, y: rect.y + rect.height },
        { name: 'w', x: rect.x, y: midY },
    ];
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}
